/**
 * AgentDetailsPage — "/agents/:agentId" (mockup: Overview / Runs / Schedule / Tools / Settings).
 * Every field is sourced from existing endpoints already used elsewhere
 * (customAgentAPI, agentRunAPI, mcpAPI) — no new backend surface.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Bot, Pencil, Play, Loader2, Square, Trash2, ArrowLeft,
  CalendarClock, Clock, CheckCircle2, XCircle, Server, Package,
  Search, Globe, FileText, Database, Calendar, FolderKanban,
  CheckSquare, Mail, Send, MessageCircle, Brain, Cpu, AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { customAgentAPI, agentRunAPI, mcpAPI } from '../services/api.js';
import { taskService } from '../services/taskApi.ts';
import { useRun } from '../context/RunContext.tsx';
import { SKILL_LIBRARY } from '../components/agentWorkspace/workspaceData.ts';
import ScheduleRunModal from '../components/agentsWorkflows/ScheduleRunModal.jsx';
import AddTaskModal from '../components/modals/AddTaskModal.tsx';
import { alpha } from '../utils/alpha';

const T = {
  page: 'var(--lyra-color-surface)',
  surface: 'var(--lyra-color-surface)',
  surface2: 'var(--lyra-color-surface-2)',
  border: 'var(--lyra-color-border)',
  ink: 'var(--lyra-color-text)',
  ink2: 'var(--app-text-2)',
  ink3: 'var(--lyra-color-text-muted)',
  ink4: 'var(--app-text-3)',
  accent: 'var(--lyra-color-primary)',
  accentDark: 'var(--app-primary-deep)',
  accentLight: 'var(--app-primary-soft)',
  green: 'var(--lyra-color-success)',
  greenBg: 'var(--lyra-color-success-bg)',
  red: 'var(--lyra-color-danger)',
  redBg: 'var(--lyra-color-danger-bg)',
  blue: 'var(--lyra-color-primary)',
};

const SKILL_BY_ID = new Map(SKILL_LIBRARY.map((s: any) => [s.id, s]));

const TABS = ['Overview', 'Runs', 'Schedule', 'Tasks', 'Tools', 'Settings'] as const;
type Tab = typeof TABS[number];

// Small, explicit icon set (avoids `import * as` from lucide-react, which
// defeats tree-shaking and balloons the bundle). Covers the built-in skill
// catalog's icons; anything else falls back to a generic package icon.
const ICON_BY_NAME: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  Search, Globe, FileText, Database, Calendar, FolderKanban,
  CheckSquare, Mail, Send, MessageCircle, Brain, Cpu, Server, Package,
};

function skillIcon(name?: string) {
  return (name && ICON_BY_NAME[name]) || Package;
}

function fmtDate(d?: string | Date | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDuration(ms: number) {
  if (!ms || ms <= 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  return `${m}m`;
}

function timeAgo(d?: string | Date | null) {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

function humanize(tag: string) {
  return tag.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const Card: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, ...style }}>{children}</div>
);

export default function AgentDetailsPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const { activeRuns, stopRun } = useRun();

  const [agent, setAgent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState<Tab>('Overview');
  const [runs, setRuns] = useState<any[]>([]);
  const [scheduled, setScheduled] = useState<any[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [mcpServers, setMcpServers] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  // customAgentAPI.list() is scoped to the logged-in user, so anything found
  // there is a real, owned CustomAgent record. The /api/agents/:id fallback
  // below covers built-in AgentDefinition agents, which live in a different
  // collection with no userId ownership — publish/enable/delete all 404
  // against those, so we gate those actions on this flag rather than showing
  // controls that silently fail.
  const [isCustomAgent, setIsCustomAgent] = useState(false);

  const loadAgent = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const d: any = await customAgentAPI.list();
      const agents: any[] = Array.isArray(d) ? d : d?.agents || [];
      let found = agents.find((a: any) => a._id === agentId || a.id === agentId);
      let ownedCustomAgent = !!found;
      if (!found) {
        try {
          const resp = await fetch(`/api/agents/${agentId}`, { headers: { accept: 'application/json' } });
          if (resp.ok) {
            const json = await resp.json();
            found = json.agent;
            ownedCustomAgent = false;
          }
        } catch (_) { /* ignore */ }
      }
      if (found) { setAgent(found); setIsCustomAgent(ownedCustomAgent); } else setNotFound(true);
    } catch (_) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { loadAgent(); }, [loadAgent]);

  useEffect(() => {
    if (!agentId) return;
    agentRunAPI.listAll({ limit: 500 }).then((d: any) => {
      const all: any[] = Array.isArray(d?.runs) ? d.runs : [];
      setRuns(all.filter((r) => String(r.agentId) === String(agentId)));
    }).catch(() => setRuns([]));

    agentRunAPI.getScheduled().then((d: any) => {
      const all: any[] = Array.isArray(d?.runs) ? d.runs : [];
      setScheduled(all.filter((r) => String(r.agentId) === String(agentId)));
    }).catch(() => setScheduled([]));

    mcpAPI.listServers().then((r: any) => setMcpServers(Array.isArray(r?.servers) ? r.servers : [])).catch(() => setMcpServers([]));
  }, [agentId]);

  const loadTasks = useCallback(() => {
    if (!agentId) return;
    setTasksLoading(true);
    taskService.getTasks({ agentId })
      .then((t) => setTasks(Array.isArray(t) ? t : []))
      .catch(() => setTasks([]))
      .finally(() => setTasksLoading(false));
  }, [agentId]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const runningEntry = useMemo(
    () => (activeRuns || []).find((r: any) => String(r.agentId) === String(agentId)),
    [activeRuns, agentId],
  );

  const attachedSkills = useMemo(() => {
    const list = agent?.skills || [];
    return list.map((s: any) => {
      const id = typeof s === 'string' ? s : s.skillId;
      const meta = SKILL_BY_ID.get(id);
      return { id, name: meta?.name || id, icon: meta?.icon };
    }).filter((s: any) => s.id);
  }, [agent]);

  const attachedMcp = useMemo(() => {
    const ids = new Set((agent?.mcpServerIds || []).map((id: any) => String(id)));
    return mcpServers.filter((s: any) => ids.has(String(s._id)));
  }, [agent, mcpServers]);

  const perf = useMemo(() => {
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = runs.filter((r) => new Date(r.createdAt).getTime() >= cutoff);
    const succeeded = recent.filter((r) => r.status === 'success');
    const successRate = recent.length > 0 ? Math.round((succeeded.length / recent.length) * 100) : null;
    const avgMs = succeeded.length > 0
      ? succeeded.reduce((sum, r) => sum + (Number(r.duration) || 0), 0) / succeeded.length
      : 0;

    const days: { label: string; count: number }[] = [];
    for (let i = 29; i >= 0; i -= 1) {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - i);
      const next = new Date(d); next.setDate(d.getDate() + 1);
      const count = recent.filter((r) => {
        const t = new Date(r.createdAt).getTime();
        return t >= d.getTime() && t < next.getTime();
      }).length;
      days.push({ label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), count });
    }
    return { total: recent.length, successRate, avgMs, days };
  }, [runs]);

  const typeTag = agent?.capabilities?.[0] ? humanize(agent.capabilities[0]) : null;

  const handleRunNow = () => {
    if (!agent) return;
    try {
      window.dispatchEvent(new CustomEvent('lyra:open-run-drawer', { detail: { agentId: agent._id || agent.id, spaceId: null } }));
    } catch (_) { /* no-op */ }
  };

  const handleToggleEnabled = async () => {
    if (!agent || busy) return;
    setBusy(true);
    const next = !(agent.enabled !== false);
    try {
      await customAgentAPI.update(agent._id || agent.id, { enabled: next });
      setAgent((a: any) => ({ ...a, enabled: next }));
      toast.success(next ? 'Agent enabled' : 'Agent disabled');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not update the agent');
    } finally {
      setBusy(false);
    }
  };

  const handleTogglePublish = async () => {
    if (!agent || busy) return;
    setBusy(true);
    try {
      const res: any = await customAgentAPI.togglePublish(agent._id || agent.id);
      setAgent((a: any) => ({ ...a, isPublished: res.isPublished, publishedAt: res.publishedAt, author: res.author }));
      toast.success(res.isPublished ? 'Published to the community 🎉' : 'Unpublished');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not update publish status');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!agent || busy) return;
    if (!window.confirm(`Delete "${agent.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await customAgentAPI.remove(agent._id || agent.id);
      toast.success('Agent deleted');
      navigate('/agents');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not delete the agent');
      setBusy(false);
    }
  };

  const handleCancelScheduled = async (runId: string) => {
    try {
      await agentRunAPI.cancelScheduled(runId);
      setScheduled((s) => s.filter((r) => r._id !== runId));
      toast.success('Scheduled run canceled');
    } catch (_) {
      toast.error('Could not cancel this run');
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '80px 0', color: T.ink3 }}>
        <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> Loading agent…
        <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
      </div>
    );
  }

  if (notFound || !agent) {
    return (
      <div style={{ padding: '60px 32px', textAlign: 'center' }}>
        <Bot size={32} color={T.ink4} style={{ margin: '0 auto 12px' }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink2, marginBottom: 10 }}>Agent not found</div>
        <button onClick={() => navigate('/agents')} style={{ border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, padding: '8px 16px', fontSize: 12.5, cursor: 'pointer' }}>
          Back to Agents
        </button>
      </div>
    );
  }

  const isRunning = !!runningEntry;
  const maxCount = Math.max(1, ...perf.days.map((d) => d.count));

  return (
    <div style={{ background: T.page, minHeight: '100%', padding: '28px 32px 60px' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <button
          onClick={() => navigate('/agents')}
          style={{ display: 'flex', alignItems: 'center', gap: 5, border: 'none', background: 'none', color: T.ink3, fontSize: 12, cursor: 'pointer', marginBottom: 14, padding: 0 }}
        >
          <ArrowLeft size={13} /> All agents
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 22, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 12, background: T.accentLight, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20,
            }}>
              {agent.icon && agent.icon !== '🤖' ? agent.icon : <Bot size={20} color={T.accent} />}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{agent.name}</div>
                <span style={{
                  fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                  background: agent.enabled !== false ? T.greenBg : T.surface2,
                  color: agent.enabled !== false ? T.green : T.ink4,
                }}>
                  {agent.enabled !== false ? '● Active' : 'Inactive'}
                </span>
                {isRunning && (
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--app-primary-soft)', color: T.blue }}>
                    Running
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: T.ink3, marginTop: 4, maxWidth: 520 }}>{agent.description || 'No description'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button
              onClick={() => navigate(`/agent-builder/${agent._id || agent.id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.ink2, borderRadius: 9, padding: '9px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
            >
              <Pencil size={13} /> Edit agent
            </button>
            {isRunning ? (
              <button
                onClick={() => runningEntry && stopRun(runningEntry._id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.red, color: 'var(--app-on-accent)', borderRadius: 9, padding: '9px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                <Square size={12} fill="var(--app-on-accent)" /> Stop
              </button>
            ) : (
              <button
                onClick={handleRunNow}
                style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accentDark, color: 'var(--app-on-accent)', borderRadius: 9, padding: '9px 15px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
              >
                <Play size={12} fill="var(--app-on-accent)" /> Run now
              </button>
            )}
          </div>
        </div>

        {/* Tabs — the active indicator is its own line under the buttons, not the
            container's border, so it can't get visually swallowed by it. */}
        <div style={{ position: 'relative', display: 'flex', gap: 4, marginBottom: 20 }}>
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 1, background: T.border }} />
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                position: 'relative', border: 'none', background: 'none', padding: '9px 14px 8px', fontSize: 12.5,
                fontWeight: tab === t ? 700 : 500, cursor: 'pointer',
                color: tab === t ? T.accentDark : T.ink3,
                borderBottom: tab === t ? `2.5px solid ${T.accentDark}` : '2.5px solid transparent',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'Overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 14 }}>About</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10.5, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Purpose</div>
                  <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>{agent.goal || agent.description || 'No description'}</div>
                </div>
                {typeTag && (
                  <div>
                    <div style={{ fontSize: 10.5, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Type</div>
                    <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3, fontWeight: 600 }}>{typeTag}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 10.5, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Created</div>
                  <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>{fmtDate(agent.createdAt)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10.5, color: T.ink4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last run</div>
                  <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3 }}>{runs[0] ? timeAgo(runs[0].createdAt) : 'Never run'}</div>
                </div>
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Tools & Integrations</div>
              {attachedSkills.length === 0 && attachedMcp.length === 0 ? (
                <div style={{ fontSize: 12, color: T.ink4 }}>No tools attached yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {attachedSkills.slice(0, 4).map((s: any) => {
                    const Icon = skillIcon(s.icon);
                    return (
                      <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.ink2 }}>
                          <Icon size={14} color={T.ink3} /> {s.name}
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, color: T.green, background: T.greenBg, borderRadius: 999, padding: '2px 8px' }}>Enabled</span>
                      </div>
                    );
                  })}
                  {attachedMcp.slice(0, Math.max(0, 4 - attachedSkills.length)).map((s: any) => (
                    <div key={s._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.ink2 }}>
                        <Server size={14} color={T.ink3} /> {s.displayName || s.name}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.green, background: T.greenBg, borderRadius: 999, padding: '2px 8px' }}>Enabled</span>
                    </div>
                  ))}
                </div>
              )}
              {(attachedSkills.length + attachedMcp.length) > 4 && (
                <button onClick={() => setTab('Tools')} style={{ border: 'none', background: 'none', color: T.accentDark, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', marginTop: 12, padding: 0 }}>
                  View all tools →
                </button>
              )}
            </Card>

            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 4 }}>Performance</div>
              <div style={{ fontSize: 11, color: T.ink4, marginBottom: 14 }}>Last 30 days</div>
              <div style={{ display: 'flex', gap: 18, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{perf.total}</div>
                  <div style={{ fontSize: 10.5, color: T.ink4 }}>Runs</div>
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: perf.successRate == null ? T.ink4 : T.green }}>{perf.successRate == null ? '—' : `${perf.successRate}%`}</div>
                  <div style={{ fontSize: 10.5, color: T.ink4 }}>Success rate</div>
                </div>
                <div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>{fmtDuration(perf.avgMs)}</div>
                  <div style={{ fontSize: 10.5, color: T.ink4 }}>Avg duration</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 44 }}>
                {perf.days.map((d, i) => (
                  <div key={i} title={`${d.label}: ${d.count}`} style={{ flex: 1, height: `${Math.max(4, (d.count / maxCount) * 44)}px`, background: T.accentLight, borderRadius: 2 }} />
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.ink4, marginTop: 6 }}>
                <span>{perf.days[0]?.label}</span>
                <span>{perf.days[perf.days.length - 1]?.label}</span>
              </div>
            </Card>
          </div>
        )}

        {tab === 'Runs' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            {runs.length === 0 ? (
              <div style={{ padding: 30, textAlign: 'center', fontSize: 12.5, color: T.ink4 }}>No runs yet.</div>
            ) : (
              <div>
                {runs.slice(0, 60).map((r) => {
                  const ok = r.status === 'success';
                  const failed = r.status === 'error' || r.status === 'stopped';
                  const notifyFailures = (r.notifyResults || []).filter((n: any) => n.status === 'failed');
                  return (
                    <div key={r._id} style={{ borderBottom: `1px solid ${T.border}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px' }}>
                        {ok ? <CheckCircle2 size={15} color={T.green} /> : failed ? <XCircle size={15} color={T.red} /> : <Loader2 size={15} color={T.blue} style={{ animation: 'spin 1s linear infinite' }} />}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12.5, color: T.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.input || 'Run'}</div>
                          <div style={{ fontSize: 11, color: T.ink4, marginTop: 2 }}>{timeAgo(r.createdAt)} · {fmtDuration(r.duration)}</div>
                          {r.output && (
                            <div style={{
                              fontSize: 11, color: T.ink4, marginTop: 3,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                            }}>
                              {String(r.output).replace(/\s+/g, ' ').trim().slice(0, 160)}
                            </div>
                          )}
                        </div>
                        <span style={{
                          fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 999,
                          background: ok ? T.greenBg : failed ? T.redBg : 'var(--app-primary-soft)',
                          color: ok ? T.green : failed ? T.red : T.blue,
                        }}>
                          {r.status}
                        </span>
                      </div>
                      {notifyFailures.map((n: any) => (
                        <div key={n.channel} style={{
                          display: 'flex', alignItems: 'center', gap: 7, margin: '0 16px 10px',
                          padding: '6px 10px', borderRadius: 8, background: T.redBg, border: `1px solid ${alpha(T.red, '30')}`,
                          fontSize: 11, color: T.red,
                        }}>
                          <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>Notification failed — {n.channel === 'telegram' ? 'Telegram' : n.channel === 'email' ? 'Email' : n.channel} not connected</span>
                          <a href={`/integrations?connect=${n.channel}`} target="_blank" rel="noopener noreferrer" style={{ color: T.red, fontWeight: 700, textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                            Connect
                          </a>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        )}

        {tab === 'Schedule' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {agent.schedule && (
              <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <CalendarClock size={16} color={T.accent} />
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>Recurring schedule</div>
                    <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2 }}>{agent.schedule}</div>
                  </div>
                </div>
              </Card>
            )}
            <Card style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Upcoming runs</div>
                <button
                  onClick={() => setShowScheduleModal(true)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accent, color: 'var(--app-on-accent)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  <CalendarClock size={13} /> Schedule run
                </button>
              </div>
              {scheduled.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: T.ink4 }}>Nothing scheduled. Also shown on the Schedule page once created.</div>
              ) : (
                scheduled.map((r) => (
                  <div key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                    <Clock size={14} color={T.ink3} />
                    <div style={{ flex: 1, fontSize: 12.5, color: T.ink2 }}>{new Date(r.scheduledAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>
                    <button onClick={() => handleCancelScheduled(r._id)} style={{ border: `1px solid ${T.border}`, background: T.surface, color: T.red, borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                ))
              )}
            </Card>
          </div>
        )}

        {showScheduleModal && agent && (
          <ScheduleRunModal
            target={agent}
            targetType="agent"
            onClose={() => setShowScheduleModal(false)}
            onScheduled={(run: any) => setScheduled((s) => [...s, run].sort((a: any, b: any) => new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()))}
          />
        )}

        {tab === 'Tasks' && (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.ink }}>Assigned tasks</div>
              <button
                onClick={() => setShowAddTaskModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.accent, color: 'var(--app-on-accent)', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                <CheckSquare size={13} /> Add task
              </button>
            </div>
            {tasksLoading ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: T.ink4 }}>Loading…</div>
            ) : tasks.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', fontSize: 12.5, color: T.ink4 }}>No tasks assigned to this agent yet — it'll see these in its system prompt and can mark them done via the "my-tasks" skill.</div>
            ) : (
              tasks.map((t: any) => (
                <div key={t._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
                  {t.status === 'Done' ? <CheckCircle2 size={14} color={T.green} /> : <CheckSquare size={14} color={T.ink3} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, textDecoration: t.status === 'Done' ? 'line-through' : 'none' }}>{t.title}</div>
                    {t.description && <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description}</div>}
                  </div>
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.status === 'Done' ? T.green : T.ink3, background: t.status === 'Done' ? T.greenBg : T.surface2, borderRadius: 999, padding: '3px 9px', flexShrink: 0 }}>{t.status}</span>
                </div>
              ))
            )}
          </Card>
        )}

        {showAddTaskModal && agentId && (
          <AddTaskModal
            isOpen={showAddTaskModal}
            onClose={() => setShowAddTaskModal(false)}
            onTaskAdded={() => { setShowAddTaskModal(false); loadTasks(); }}
            mode="task-management"
            presetAgentId={agentId}
          />
        )}

        {tab === 'Tools' && (
          <Card>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Tools & Integrations</div>
            {attachedSkills.length === 0 && attachedMcp.length === 0 ? (
              <div style={{ fontSize: 12.5, color: T.ink4 }}>No tools attached. Add some in the Agent Builder.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                {attachedSkills.map((s: any) => {
                  const Icon = skillIcon(s.icon);
                  return (
                    <div key={s.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.ink2 }}>
                        <Icon size={14} color={T.ink3} /> {s.name}
                      </div>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.green, background: T.greenBg, borderRadius: 999, padding: '2px 8px' }}>Enabled</span>
                    </div>
                  );
                })}
                {attachedMcp.map((s: any) => (
                  <div key={s._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${T.border}`, borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.ink2 }}>
                      <Server size={14} color={T.ink3} /> {s.displayName || s.name}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.green, background: T.greenBg, borderRadius: 999, padding: '2px 8px' }}>Enabled</span>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => navigate(`/agent-builder/${agent._id || agent.id}`)}
              style={{ border: 'none', background: 'none', color: T.accentDark, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', marginTop: 16, padding: 0 }}
            >
              Manage tools in Agent Builder →
            </button>
          </Card>
        )}

        {tab === 'Settings' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
            <Card>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 14 }}>Agent settings</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  ['Name', agent.name],
                  ['Model', agent.defaultModel || agent.model || '—'],
                  ['Max tokens', agent.maxTokens ?? '—'],
                  ['Temperature', agent.temperature ?? '—'],
                ].map(([label, val]) => (
                  <div key={label as string} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5 }}>
                    <span style={{ color: T.ink4 }}>{label}</span>
                    <span style={{ color: T.ink2, fontWeight: 600 }}>{String(val)}</span>
                  </div>
                ))}
                {isCustomAgent && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                    <span style={{ color: T.ink3 }}>{agent.enabled !== false ? 'Agent is active' : 'Agent is disabled'}</span>
                    <button
                      onClick={handleToggleEnabled}
                      disabled={busy || agent.isLyran}
                      style={{
                        border: `1px solid ${T.border}`, background: T.surface, borderRadius: 7, padding: '6px 12px',
                        fontSize: 11.5, fontWeight: 600, cursor: busy || agent.isLyran ? 'not-allowed' : 'pointer', color: T.ink2,
                      }}
                    >
                      {agent.enabled !== false ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                )}
              </div>
            </Card>

            {!isCustomAgent && (
              <Card>
                <div style={{ fontSize: 12.5, color: T.ink3 }}>
                  This is a built-in agent, not one you created — it can't be enabled/disabled, published, or deleted here.
                </div>
              </Card>
            )}

            {isCustomAgent && !agent.isLyran && (
              <Card>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink, marginBottom: 6 }}>Community</div>
                <div style={{ fontSize: 12, color: T.ink3, marginBottom: 12 }}>
                  {agent.isPublished ? 'This agent is published — anyone can find and add it from the Community page.' : 'Publish this agent so others can discover and add it from the Community page.'}
                </div>
                <button
                  onClick={handleTogglePublish}
                  disabled={busy}
                  style={{
                    border: `1px solid ${agent.isPublished ? T.border : T.accent}`, borderRadius: 8, padding: '8px 14px',
                    background: agent.isPublished ? T.surface : T.accentDark, color: agent.isPublished ? T.ink2 : 'var(--lyra-color-surface)',
                    fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {agent.isPublished ? 'Unpublish' : 'Publish to community'}
                </button>
              </Card>
            )}

            {isCustomAgent && !agent.isLyran && (
              <Card style={{ borderColor: 'var(--app-danger-border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.red, marginBottom: 6 }}>Danger zone</div>
                <div style={{ fontSize: 12, color: T.ink3, marginBottom: 12 }}>Deleting an agent cannot be undone.</div>
                <button
                  onClick={handleDelete}
                  disabled={busy}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: T.red, color: 'var(--app-on-accent)', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer' }}
                >
                  <Trash2 size={13} /> Delete agent
                </button>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
